import { useState } from 'react';
import { api, type League, type Team } from '../lib/api';
import SquadListDoc from './SquadListDoc';
import { docsFor } from '../lib/league-docs';
import { SOCIETY_SQUAD_CAP } from '../lib/squad-list';

/**
 * Where a player goes for the rules and the seed ratings.
 *
 * ON THE TABLE, which is the screen the app opens on. The league asked for the
 * documents to be somewhere a player can "always" reach, and the honest reading
 * of that is the landing page rather than a sixth tab: the bar already carries
 * five, and on a 390px phone a sixth turns every label into an abbreviation.
 *
 * The two documents are deliberately different KINDS of thing, and the card
 * shows it. The rules are a file, published once and downloaded. The squad list
 * is built from the season as it stands the moment it is opened, so it cannot
 * disagree with the table above it — which is the entire reason it is not also
 * a file. See league-docs.ts.
 */
export default function LeagueDocs({
  seasonId,
  league,
  scoringSystem,
  seasonName,
}: {
  seasonId?: number;
  league?: League | null;
  scoringSystem?: string | null;
  seasonName?: string | null;
}) {
  const [listing, setListing] = useState(false);
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const files = docsFor(league?.slug, scoringSystem);
  const tiered = league?.playerGrading === 'tier';
  const squadCap =
    league?.slug === 'society-team-league' && scoringSystem === 'season2'
      ? SOCIETY_SQUAD_CAP
      : null;

  /*
   * The squads are fetched when the list is ASKED FOR, not when the table
   * loads. Every visitor to the league would otherwise pay for a document most
   * of them will never open, on the one screen whose speed matters most.
   */
  const openList = () => {
    setError(null);
    if (teams) {
      setListing(true);
      return;
    }
    if (!seasonId) return;
    api
      .teams(seasonId)
      .then((d) => {
        setTeams(d.teams);
        setListing(true);
      })
      .catch((e: Error) => setError(e.message));
  };

  // A season with no rule book and no squads has no card, rather than a card
  // explaining that it has nothing.
  if (files.length === 0 && !seasonId) return null;

  return (
    <section className="card docs-card">
      <div className="card-head">
        <h2>League documents</h2>
      </div>

      <ul className="docs">
        {files.map((doc) => (
          <li key={doc.href}>
            {/*
              `download` names the file the player ends up with. Without it a
              phone saves "society-team-league-season-2-rules.pdf", which is the
              path we happened to store it under rather than what the document
              is called.
            */}
            <a className="doc" href={doc.href} download={doc.filename}>
              <span className="doc-title">{doc.title}</span>
              <span className="doc-desc">{doc.description}</span>
              <span className="doc-kind">PDF</span>
            </a>
          </li>
        ))}

        {seasonId && (
          <li>
            <button type="button" className="doc" onClick={openList}>
              <span className="doc-title">Seed ratings &amp; squads</span>
              <span className="doc-desc">
                Every squad with the seed each player was rated at — the figure the pair cap is
                judged on. Built from the league as it stands right now, so it is never out of date.
              </span>
              <span className="doc-kind">Live</span>
            </button>
          </li>
        )}
      </ul>

      {error && <p className="muted">Could not load the squads: {error}</p>}

      {listing && teams && (
        <SquadListDoc
          teams={teams}
          leagueName={league?.name ?? 'League'}
          seasonName={seasonName ?? ''}
          cap={squadCap}
          tiered={tiered}
          onClose={() => setListing(false)}
        />
      )}
    </section>
  );
}
